// Shared bar-chart primitives for the report pages. Charts sit above the
// tables they summarize — the table stays the source of truth, the bars make
// the shape legible at a glance. Palette follows the app: primary blue for
// the current/actual series, slate for the prior/reference one, emerald and
// red only where a direction is genuinely good or bad. Callers never chart
// an empty period — these components assume real values and render nothing
// when a pair is missing a side.
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Delta } from '../PeriodCompare';

export const barColors = {
  current: '#0284c7', // primary-600 — this period / actual
  prior: '#94a3b8', // slate-400 — prior period / reference
  good: '#16a34a', // success-600
  bad: '#dc2626', // danger-600
};

// Dollar labels at bar ends stay short — the exact figure lives in the table
// (and the tooltip): cents under $1,000, whole dollars above.
export const shortMoney = (cents: number) => {
  const abs = Math.abs(cents) / 100;
  const body = abs.toLocaleString('en-US', abs >= 1000 ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${cents < 0 ? '-' : ''}$${body}`;
};

export const signedShortMoney = (cents: number) =>
  `${cents >= 0 ? '+' : ''}${shortMoney(cents)}`;

const tooltipStyle = {
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '12px',
} as const;

const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

// A value label at the outer end of a horizontal bar — right of positive
// bars, left of negative ones, so the number never sits inside the bar.
const renderEndLabel = (props: any, format: (v: number) => string) => {
  const { x, y, width, height, value } = props;
  if (value === null || value === undefined) return null;
  const v = Number(value);
  const negative = v < 0;
  return (
    <text
      x={negative ? Number(x) - 5 : Number(x) + Number(width) + 5}
      y={Number(y) + Number(height) / 2}
      dy={3.5}
      textAnchor={negative ? 'end' : 'start'}
      fontSize={11}
      fill="#334155"
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {format(v)}
    </text>
  );
};

// Pad the number axis so the value label printed just past the longest bar
// has room to render in full instead of being clipped mid-number ("$214,77").
// The end label lives OUTSIDE the bar, so the longest bar must stop well short
// of the axis edge; 1.35x left too little for a wide figure like "$214,770".
// Zero is always included so bar lengths stay honest.
const paddedDomain: [(min: number) => number, (max: number) => number] = [
  (min) => Math.min(0, min * 1.7),
  (max) => Math.max(0, max * 1.7),
];

export interface HBarItem {
  name: string;
  value: number;
  // Extra context for the tooltip, e.g. "$41.00 → $56.50".
  detail?: string;
}

interface HBarChartProps {
  items: HBarItem[];
  format: (v: number) => string;
  color: (item: HBarItem) => string;
  seriesLabel: string;
}

function HBarChart({ items, format, color, seriesLabel }: HBarChartProps) {
  if (items.length === 0) return null;
  const hasNegative = items.some((i) => i.value < 0);
  return (
    <div className="min-w-0">
      <ResponsiveContainer width="100%" height={items.length * 28 + 12}>
        {/* Right margin gives the end-of-bar value label room to render in
            full without the SVG clipping it; the y-axis width already holds
            the left side, with a small margin for a negative-bar label. */}
        <BarChart data={items} layout="vertical" margin={{ top: 4, right: 56, left: 12, bottom: 4 }}>
          <XAxis type="number" hide domain={paddedDomain} />
          <YAxis
            type="category"
            dataKey="name"
            width={110}
            interval={0}
            tick={{ fontSize: 11, fill: '#475569' }}
            tickFormatter={(name: string) => truncate(name, 17)}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            cursor={{ fill: '#f1f5f9' }}
            contentStyle={tooltipStyle}
            formatter={(v: number, _name: string, entry: any) => [
              entry?.payload?.detail ?? format(Number(v)),
              seriesLabel,
            ]}
          />
          {hasNegative && <ReferenceLine x={0} stroke="#cbd5e1" />}
          <Bar dataKey="value" name={seriesLabel} barSize={14} radius={[0, 4, 4, 0]} isAnimationActive={false}>
            {items.map((item) => (
              <Cell key={item.name} fill={color(item)} />
            ))}
            <LabelList dataKey="value" content={(p: any) => renderEndLabel(p, format)} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// A ranked list as horizontal bars: item names on the y-axis, the value
// labelled at the bar's end. Negative values read red — a loss is a loss.
export function HBarList({ items, format, seriesLabel }: {
  items: HBarItem[];
  format: (v: number) => string;
  seriesLabel: string;
}) {
  return (
    <HBarChart
      items={items}
      format={format}
      seriesLabel={seriesLabel}
      color={(i) => (i.value < 0 ? barColors.bad : barColors.current)}
    />
  );
}

// Signed movement bars: green growing right for improvements, red growing
// left for declines, zero line in between.
export function DeltaBars({ items, format, goodWhenUp = true }: {
  items: Array<{ name: string; delta: number; detail?: string }>;
  format: (v: number) => string;
  goodWhenUp?: boolean;
}) {
  return (
    <HBarChart
      items={items.map((i) => ({ name: i.name, value: i.delta, detail: i.detail }))}
      format={format}
      seriesLabel="Change"
      color={(i) => ((i.value > 0) === goodWhenUp ? barColors.good : barColors.bad)}
    />
  );
}

export interface PairedMetric {
  label: string;
  a: number | null | undefined;
  b: number | null | undefined;
  kind: 'cents' | 'points';
  goodWhenUp: boolean;
}

// One small card per compared metric: period A vs B as two horizontal bars
// on the metric's own scale (dollars and points never share an axis), with
// the delta annotated beside the label. Metrics missing a side are skipped —
// the table below still lists them.
export function PairedBars({ metrics, aLabel, bLabel }: {
  metrics: PairedMetric[];
  aLabel: string;
  bLabel: string;
}) {
  const complete = metrics.filter(
    (m) => m.a !== null && m.a !== undefined && m.b !== null && m.b !== undefined
  );
  if (complete.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {complete.map((m) => {
        const format = m.kind === 'cents' ? shortMoney : (v: number) => `${v.toFixed(1)}%`;
        const data = [
          { name: aLabel, value: m.a as number },
          { name: bLabel, value: m.b as number },
        ];
        return (
          <div key={m.label} className="rounded-lg bg-slate-50 p-3 min-w-0">
            <div className="flex items-baseline justify-between gap-2 mb-1">
              {/* The full metric name — wrap rather than clip ("Opera…"). */}
              <span className="text-xs font-medium text-slate-600 break-words">{m.label}</span>
              <Delta now={m.a} prev={m.b} kind={m.kind} goodWhenUp={m.goodWhenUp} />
            </div>
            <ResponsiveContainer width="100%" height={64}>
              {/* Right margin holds the full dollar value past the bar end
                  (was clipping to "$214,77"); small left margin keeps a
                  negative-bar label off the y-axis ticks. */}
              <BarChart data={data} layout="vertical" margin={{ top: 2, right: 60, left: 8, bottom: 2 }}>
                <XAxis type="number" hide domain={paddedDomain} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={104}
                  interval={0}
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={(name: string) => truncate(name, 18)}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ fill: '#f1f5f9' }}
                  contentStyle={tooltipStyle}
                  formatter={(v: number) => [format(Number(v)), m.label]}
                />
                <Bar dataKey="value" name={m.label} barSize={14} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                  <Cell fill={barColors.current} />
                  <Cell fill={barColors.prior} />
                  <LabelList dataKey="value" content={(p: any) => renderEndLabel(p, format)} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      })}
    </div>
  );
}

// Grouped vertical bars — e.g. stores on the x-axis with a bar per metric.
// Mirrors the Food Cost category chart's look (grid, quiet axes, legend).
export function GroupedBars({ data, xKey, series, format, axisFormat, height = 240 }: {
  data: Array<Record<string, any>>;
  xKey: string;
  series: Array<{ key: string; label: string; color: string }>;
  format: (v: number) => string;
  axisFormat?: (v: number) => string;
  height?: number;
}) {
  if (data.length === 0) return null;
  const hasNegative = data.some((row) => series.some((s) => Number(row[s.key]) < 0));
  return (
    <div className="min-w-0">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 12, fill: '#64748b' }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
          />
          <YAxis
            tickFormatter={(v: number) => (axisFormat ?? format)(Number(v))}
            tick={{ fontSize: 12, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            cursor={{ fill: '#f1f5f9' }}
            contentStyle={tooltipStyle}
            formatter={(v: number) => format(Number(v))}
          />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          {hasNegative && <ReferenceLine y={0} stroke="#cbd5e1" />}
          {series.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
